import React from "react";
import {
  ButtonBase,
  Divider,
  Stack,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip as MuiTooltip,
  Box,
} from "@mui/material";
import { AlertColors } from "../../../components/alerting/alert_enums";
import LandingCard from "./landing_card";
import { TOOLTIPS_EN } from "../../../enums/tooltip_enum_EN";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import NotificationsIcon from "@mui/icons-material/Notifications";

const HealthCard = ({
  criticals,
  errors,
  warnings,
  healthy,
  pending,
  handleFilterClick,
  includeMutes,
  setIncludeMutes,
}) => {
  const handleToggleChange = (event, newValue) => {
    if (newValue !== null) {
      setIncludeMutes(newValue === "normal");
    }
  };

  const content = [
    {
      title: "Healthy",
      value: healthy,
      color: AlertColors.SUCCESS,
      filter: ["status ok"],
    },
    {
      title: "Error",
      value: errors,
      color: AlertColors.ERROR,
      filter: ["error"],
      urgent: true,
    },
    {
      title: "Pending",
      value: pending,
      color: AlertColors.PENDING,
      filter: ["pending"],
    },
  ];

  const totalValue = content.reduce((sum, item) => sum + (item.value || 0), 0) || 1;

  // Highlight only the most severe category that has sites: critical first, then error
  const highlightedTitle = Number(errors) > 0 ? "Error" : null;

  const toggleButton = (
    <ToggleButtonGroup value={includeMutes ? "normal" : "mutes"} exclusive onChange={handleToggleChange} size="small">
      <MuiTooltip title="Sites without mutes" arrow>
        <ToggleButton value="normal" sx={{ px: 1, py: 0.5 }}>
          <NotificationsIcon sx={{ fontSize: 16 }} />
        </ToggleButton>
      </MuiTooltip>
      <MuiTooltip title="Sites with mutes" arrow>
        <ToggleButton value="mutes" sx={{ px: 1, py: 0.5 }}>
          <NotificationsOffIcon sx={{ fontSize: 16 }} />
        </ToggleButton>
      </MuiTooltip>
    </ToggleButtonGroup>
  );

  return (
    <LandingCard title="Site Health" tooltip={TOOLTIPS_EN.SITE_HEALTH} action={toggleButton} sx={{ mt: 2 }}>
      <Stack spacing={2}>
        <Stack spacing={1}>
          {content.map((item) => {
            const widthPercent = (item.value / totalValue) * 100;
            const isHighlighted = item.urgent && item.title === highlightedTitle;

            return (
              <ButtonBase
                key={item.title}
                onClick={() =>
                  handleFilterClick([
                    { type: "status", value: item.filter },
                    ...(item.title === "Pending" ? [] : [{ type: "commissioning_completed", value: true }]),
                  ])
                }
                sx={{
                  width: "100%",
                  justifyContent: "flex-start",
                  p: 1,
                  borderRadius: 1,
                  backgroundColor: isHighlighted ? `${item.color}15` : "transparent",
                  borderLeft: isHighlighted ? `3px solid ${item.color}` : "3px solid transparent",
                  "&:hover": { backgroundColor: isHighlighted ? `${item.color}25` : "#f7f7f7" },
                }}
              >
                <Stack direction="row" alignItems="center" sx={{ width: "100%" }} spacing={2}>
                  <Typography
                    variant="body2"
                    sx={{ width: 70, fontWeight: isHighlighted ? 700 : 500, textAlign: "left" }}
                  >
                    {item.title}
                  </Typography>

                  <Box
                    sx={{
                      flexGrow: 1,
                      height: 10,
                      backgroundColor: "#eee",
                      borderRadius: 5,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        width: `${widthPercent}%`,
                        height: "100%",
                        backgroundColor: item.color,
                      }}
                    />
                  </Box>

                  <Typography
                    variant="body2"
                    sx={{ minWidth: 70, textAlign: "left", fontWeight: isHighlighted ? 700 : 400 }}
                  >
                    {item.value} sites
                  </Typography>
                </Stack>
              </ButtonBase>
            );
          })}
        </Stack>

        <Divider />
      </Stack>
    </LandingCard>
  );
};

export default HealthCard;
